import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { MESSAGE_ROLES, OP_KINDS } from '../../../../constants/aiAnalytics';
import { messageOpKey } from '../../../../services/aiAnalytics/operationResume.service';
import { AI_COLORS, CHAT_CHART_HEIGHT } from '../aiAnalyticsConstants';
import { messageIdOf } from '../services/aiDataUtils';
import { buildTableSpec, detectChartType, isTableSpec, TABLE_DISPLAY_TYPE } from '../services/chartTypeUtils';
import { exportRowsToExcel } from '../services/excelExport';

import ChartTypeToggle from './ChartTypeToggle';
import { chartTypePrompt, deriveMessageDisplayData, exportBaseName, REFINE_MODES } from './ChatMessage.service';
import InlineProgress from './InlineProgress';
import MessageActionButtons from './MessageActionButtons';
import MessageFeedback from './MessageFeedback';
import { isRateableAnswer } from './MessageFeedback.service';
import RefineInputRow from './RefineInputRow';
import ResultMetaBar from './ResultMetaBar';
import SqlQueryAccordion from './SqlQueryAccordion';
import SqlResultTable from './SqlResultTable';
import VegaLiteChart from './VegaLiteChart';

export default function ChatMessage({
  message,
  question,
  threadTitle,
  onGenerateChart,
  onRefineChart,
  onRefineQuery,
  onSaveChart,
  onLoadData,
  onRefreshData,
  onSetChartSpec,
  onRateMessage
}) {
  const msgId = messageIdOf(message);
  const [chartSpec, setChartSpec] = useState(message.chartSpec || null);
  const [loadedData, setLoadedData] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [refineMode, setRefineMode] = useState(null);
  const [refineText, setRefineText] = useState('');
  const [chartVisible, setChartVisible] = useState(!message.chartSpec);
  const autoLoadStartedRef = useRef(false);

  const pendingOp = useSelector(state => state.aiAnalytics.pendingOps[messageOpKey(msgId)]);
  const isGeneratingChart = pendingOp?.kind === OP_KINDS.GENERATE_CHART;
  const isRefiningChart = pendingOp?.kind === OP_KINDS.REFINE_CHART;
  const isRefreshingData = pendingOp?.kind === OP_KINDS.REFRESH_DATA;

  useEffect(
    function syncPersistedChartSpec() {
      if (!message.chartSpec) {
        return;
      }
      setChartSpec(previousSpec => (message.chartSpec === previousSpec ? previousSpec : message.chartSpec));
    },
    [message.chartSpec]
  );

  const isUser = message.role === MESSAGE_ROLES.USER;
  const display = deriveMessageDisplayData(message, loadedData);
  const { hasInlineData, hasLoadedData, showData } = display;
  const exportDisabled = display.rows.length === 0;

  function handleExportExcel() {
    if (exportDisabled) {
      return;
    }
    exportRowsToExcel(display.columns, display.rows, exportBaseName(question, threadTitle));
  }

  const handleLoadData = useCallback(
    async function() {
      if (!onLoadData || !msgId || isLoadingData) {
        return;
      }
      setIsLoadingData(true);
      try {
        const result = await onLoadData(msgId);
        if (result) {
          setLoadedData(result);
        }
      } finally {
        setIsLoadingData(false);
      }
    },
    [onLoadData, msgId, isLoadingData]
  );

  const shouldAutoLoadChartData = Boolean(
    chartVisible &&
      chartSpec &&
      !hasInlineData &&
      !hasLoadedData &&
      !isLoadingData &&
      message.sql &&
      msgId &&
      onLoadData
  );

  useEffect(
    function autoLoadChartData() {
      if (!shouldAutoLoadChartData || autoLoadStartedRef.current) {
        return;
      }
      autoLoadStartedRef.current = true;
      handleLoadData();
    },
    [shouldAutoLoadChartData, handleLoadData]
  );

  async function handleRefreshData() {
    if (!onRefreshData || !msgId || isRefreshingData) {
      return;
    }
    const result = await onRefreshData(msgId);
    if (result && hasLoadedData) {
      setLoadedData(result);
    }
  }

  async function handleGenerateChart() {
    if (!onGenerateChart || !msgId || isGeneratingChart) {
      return;
    }
    const spec = await onGenerateChart(msgId);
    if (spec) {
      setChartSpec(spec);
      setChartVisible(true);
    }
  }

  function handleSaveChart() {
    if (onSaveChart && chartSpec) {
      onSaveChart(msgId, message.content || message.sql || 'AI Analytics Chart', chartSpec, message.sql);
    }
  }

  async function handleChartTypeChange(type) {
    if (!msgId) {
      return;
    }
    if (type === TABLE_DISPLAY_TYPE) {
      const tableSpec = buildTableSpec();
      setChartSpec(tableSpec);
      setChartVisible(true);
      if (onSetChartSpec) {
        onSetChartSpec(msgId, tableSpec);
      }
      return;
    }
    if (!onRefineChart || isRefiningChart) {
      return;
    }
    const spec = await onRefineChart(msgId, chartTypePrompt(type));
    if (spec) {
      setChartSpec(spec);
      setChartVisible(true);
    }
  }

  function openRefine(mode) {
    setRefineMode(mode);
    setRefineText('');
  }

  function cancelRefine() {
    setRefineMode(null);
    setRefineText('');
  }

  async function submitRefine() {
    const text = refineText.trim();
    if (!text) {
      return;
    }
    if (refineMode === REFINE_MODES.QUERY && onRefineQuery) {
      onRefineQuery(text, message);
      cancelRefine();
    } else if (refineMode === REFINE_MODES.CHART && onRefineChart && msgId) {
      const spec = await onRefineChart(msgId, text);
      if (spec) {
        setChartSpec(spec);
        setChartVisible(true);
      }
      cancelRefine();
    }
  }

  if (isUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Box
          sx={{
            backgroundColor: AI_COLORS.brand,
            color: AI_COLORS.onBrand,
            borderRadius: '12px 12px 0 12px',
            px: 2,
            py: 1,
            maxWidth: '70%'
          }}
        >
          <Typography variant="body1">{message.content}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
      <Box
        sx={{
          backgroundColor: message.isError ? AI_COLORS.errorSurface : AI_COLORS.surfaceMuted,
          borderRadius: '12px 12px 12px 0',
          px: 2,
          py: 1,
          maxWidth: '85%',
          width: '100%'
        }}
      >
        {message.content && (
          <Typography
            variant="body1"
            sx={{ mb: 1, color: message.isError ? AI_COLORS.errorText : 'inherit', whiteSpace: 'pre-wrap' }}
          >
            {message.content}
          </Typography>
        )}

        {message.reasoning && (
          <Accordion sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Reasoning</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {message.reasoning}
              </Typography>
            </AccordionDetails>
          </Accordion>
        )}

        {message.sql && <SqlQueryAccordion sql={message.sql} />}

        {showData && (
          <Accordion sx={{ mb: 1 }} defaultExpanded={hasInlineData}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Results ({display.rows.length} rows)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {display.rows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  The query returned no rows.
                </Typography>
              ) : (
                <SqlResultTable columns={display.columns} rows={display.rows} />
              )}
              {isRefreshingData && <InlineProgress label={pendingOp.label} dense />}
              <ResultMetaBar
                executionTimeMs={display.executionTimeMs}
                executedAt={display.executedAt}
                truncated={display.truncated}
                rowCount={display.rows.length}
                onRefresh={message.sql ? handleRefreshData : null}
                refreshing={isRefreshingData}
                onExport={handleExportExcel}
                exportDisabled={exportDisabled}
              />
            </AccordionDetails>
          </Accordion>
        )}

        {chartSpec && chartVisible && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Box sx={{ mb: 1 }}>
              <ChartTypeToggle
                value={detectChartType(chartSpec)}
                onChange={handleChartTypeChange}
                busy={isRefiningChart}
              />
            </Box>
            {isTableSpec(chartSpec) ? (
              <SqlResultTable
                columns={display.columns}
                rows={display.rows}
                footerLeft={
                  <ResultMetaBar
                    executionTimeMs={display.executionTimeMs}
                    executedAt={display.executedAt}
                    truncated={display.truncated}
                    rowCount={display.rows.length}
                    onExport={handleExportExcel}
                    exportDisabled={exportDisabled}
                  />
                }
              />
            ) : (
              <>
                <VegaLiteChart spec={chartSpec} data={display.rows} height={CHAT_CHART_HEIGHT} />
                <ResultMetaBar
                  executionTimeMs={display.executionTimeMs}
                  executedAt={display.executedAt}
                  truncated={display.truncated}
                  rowCount={display.rows.length}
                  onExport={handleExportExcel}
                  exportDisabled={exportDisabled}
                />
              </>
            )}
          </Box>
        )}

        {!message.isError && msgId && (
          <MessageActionButtons
            hasSql={Boolean(message.sql)}
            hasChartSpec={Boolean(chartSpec)}
            hasData={hasInlineData || hasLoadedData}
            chartVisible={chartVisible}
            isLoadingData={isLoadingData}
            isGeneratingChart={isGeneratingChart}
            isRefiningChart={isRefiningChart}
            isRefreshingData={isRefreshingData}
            refineMode={refineMode}
            canRefineQuery={Boolean(onRefineQuery)}
            canRefineChart={Boolean(onRefineChart)}
            onLoadData={handleLoadData}
            onGenerateChart={handleGenerateChart}
            onToggleChartVisible={() => setChartVisible(v => !v)}
            onOpenRefine={openRefine}
            onSaveChart={handleSaveChart}
          />
        )}

        {onRateMessage && msgId && isRateableAnswer(message) && (
          <MessageFeedback
            feedback={message.feedback}
            onChange={(rating, comment) => onRateMessage(msgId, rating, comment)}
          />
        )}

        {(isGeneratingChart || isRefiningChart || (isRefreshingData && !showData)) && (
          <InlineProgress label={pendingOp.label} />
        )}

        {refineMode && (
          <RefineInputRow
            mode={refineMode}
            value={refineText}
            onChange={setRefineText}
            onSubmit={submitRefine}
            onCancel={cancelRefine}
            busy={isRefiningChart}
          />
        )}
      </Box>
    </Box>
  );
}
